/**
 * Catalogue des libellés — ENF-012.
 *
 * Tous les textes visibles vivent ici, sans interpolation implicite. Le
 * catalogue prépare une éventuelle traduction sans la réaliser : la version 1
 * est en français, quelle que soit la langue du système.
 */

export const APP_NAME = 'RoadbookUltra';

/** Inventaire fermé de EF-NAV-001 : deux onglets et le mode course. */
export const SCREENS = {
  setup: 'Import et paramètres',
  roadbook: 'Roadbook',
  race: 'Mode course',
} as const;

/** Surfaces temporaires de EF-NAV-001. */
export const SHEETS = {
  actions: 'Actions',
  goto: 'Aller à un point',
  recalc: 'Recalculer',
  filters: 'Filtres',
  manualPoi: 'POI manuel',
  purge: 'Purger les POI obsolètes',
  searchProgress: 'Recherche en cours',
  raceActions: 'Actions',
} as const;

export const ACTIONS = {
  importGpx: 'Importer un GPX',
  importRoadbook: 'Importer un roadbook',
  importParameters: 'Importer des paramètres',
  exportRoadbook: 'Exporter',
  exportParameters: 'Exporter les paramètres',
  resumeSession: 'Reprendre la session précédente',
  resumeSearch: 'Reprendre la recherche interrompue',
  search: 'Rechercher les POI',
  searchZone: 'Rechercher cette zone',
  compute: 'Calculer',
  cancel: 'Annuler',
  confirm: 'Confirmer',
  back: 'Retour',
  close: 'Fermer',
  save: 'Enregistrer',
  saveSettings: 'Enregistrer les réglages',
  purge: 'Purger les POI obsolètes',
  undo: 'Annuler la purge',
  enterRace: 'Entrer en mode course',
  leaveRace: 'Quitter le mode course',
  correctPosition: 'Corriger ma position',
  recomputeNow: 'Recalculer ma position',
  lockScreen: 'Verrouiller l’écran',
  unlockScreen: 'Déverrouiller l’écran',
  goToKilometer: 'Aller au kilomètre',
  goToStart: 'Aller au départ',
  goToFinish: 'Aller à l’arrivée',
  goToNextPinned: 'Aller au prochain POI épinglé',
  openNavigation: 'Ouvrir la navigation dans la liste',
  addManualPoi: 'Ajouter un POI manuel',
  deleteManualPoi: 'Supprimer définitivement ce POI',
  exclude: 'Exclure',
  restore: 'Rétablir dans le roadbook',
  pin: 'Épingler',
  unpin: 'Désépingler',
  annotate: 'Annoter',
  openInMaps: 'Afficher sur une carte',
  openActions: 'Ouvrir les actions',
  showDetail: 'Afficher le détail',
  hideDetail: 'Masquer le détail',
  recalcWithNewStart: 'Recalculer avec un nouvel horaire',
  checkUpdate: 'Vérifier les mises à jour',
  activateUpdate: 'Activer la mise à jour',
  requestPersistence: 'Demander le stockage persistant',
  retryOffline: 'Réessayer la préparation hors ligne',
  resetParameters: 'Réinitialiser les paramètres',
  resetRoadbook: 'Réinitialiser le roadbook',
  resetAll: 'Réinitialisation complète',
  takeOver: 'Prendre la main',
  about: 'À propos',
} as const;

export const EMPTY_STATES = {
  noRoute: {
    title: 'Aucun parcours chargé',
    body: 'Importez un fichier GPX ou un roadbook déjà exporté pour commencer.',
  },
  noSearch: {
    title: 'Aucune recherche lancée',
    body: 'Le parcours est chargé. Lancez une recherche de POI, ou ajoutez des POI manuellement.',
  },
  emptySearch: {
    title: 'La recherche n’a retourné aucun POI',
    body: 'Élargissez le corridor, la portion recherchée ou les catégories actives.',
  },
  notComputed: {
    title: 'Roadbook non calculé',
    body: 'Touchez Calculer pour obtenir les D+ et les heures de passage estimées.',
  },
  allFiltered: {
    title: 'Tous les POI sont masqués par les filtres',
    body: 'Réinitialisez les filtres pour retrouver la liste complète.',
  },
  allExcluded: {
    title: 'Tous les POI sont exclus',
    body: 'Rétablissez un POI depuis son détail pour le faire réapparaître.',
  },
} as const;

/** Sous-titres des entrées de la feuille d'actions — EF-NAV-006. */
export const SHEET_HINTS = {
  race: 'Le prochain POI, en grand',
  goto: 'Kilomètre, départ, arrivée, épinglé, catégorie',
  recalc: 'Remplace l’horaire de départ et replanifie les arrêts',
} as const;

export const MARKERS = {
  staleValues: 'Valeurs périmées — relancez le calcul',
  partialResult: 'Résultat de recherche partiel',
  heterogeneousParameters: 'Zones recherchées avec des paramètres différents',
  noSleep: 'Calcul sans période de sommeil',
  elevationUnavailable: 'Altitude indisponible',
  elevationUnavailableField: 'D+ indisponible',
  segmentGaps: 'Écarts entre segments',
  pointAnomalies: 'Écarts anormaux entre points consécutifs',
  timezone:
    'Les heures sont locales au départ : un changement de fuseau ou d’heure légale n’est pas corrigé.',
  outOfSearchedPortion: 'Hors portion recherchée',
  categoriesChanged: 'Catégories modifiées depuis la recherche précédente',
  customersOnly: 'Réservé aux clients',
  missingSearch: 'Recherche de POI incomplète',
  readOnly:
    'Une autre fenêtre de l’application détient la session : celle-ci est en lecture seule.',
} as const;

export const PRESENCE = {
  present: 'Présent dans le dernier résultat',
  absentFromResult: 'Absent du dernier résultat',
  rejectedByFilter: 'Écarté par un filtre',
  rangeNotCovered: 'Zone non recherchée',
} as const;

export const ERRORS = {
  busy: (operation: string, cancelAction: string): string =>
    `Opération en cours : ${operation}. Terminez-la ou utilisez « ${cancelAction} » avant d’en lancer une autre.`,
  raceNeedsResult:
    'Le mode course exige un résultat de calcul. Touchez Calculer, puis entrez en mode course.',
  raceDuringOperation: 'Le mode course ne peut être activé pendant une opération longue.',
  searchOffline: 'La recherche de POI est indisponible hors ligne.',
  searchDuringRace: 'La recherche de POI est indisponible depuis le mode course.',
  invalidParameters:
    'Les paramètres actifs sont invalides : aucun résultat existant n’a été remplacé.',
  updateRefusedRace:
    'La mise à jour ne peut être activée pendant le mode course. Elle sera reproposée à la sortie.',
  updateRefusedBusy:
    'La mise à jour ne peut être activée pendant une opération longue. Elle sera reproposée ensuite.',
  saveFailed:
    'La sauvegarde locale a échoué. La dernière version valide est conservée ; exportez votre roadbook.',
  readOnlyContext: 'Cette fenêtre est en lecture seule : prenez la main pour modifier le roadbook.',
} as const;

export const CONFIRMATIONS = {
  replaceRoadbook:
    'Le parcours, les POI, la curation et les résultats courants seront remplacés. Voulez-vous les exporter d’abord ?',
  sameDocument: 'Ce fichier est une autre version du même roadbook.',
  differentDocument: 'Ce fichier est un roadbook différent.',
  deleteManualPoi:
    'Cette suppression retire réellement les données du document et n’est pas annulable.',
  resetParameters:
    'Les paramètres reprendront leurs valeurs par défaut. Le roadbook, sa curation et ses résultats sont conservés.',
  resetRoadbook:
    'Le parcours, les POI, la curation, les notes et les résultats seront effacés. Les paramètres et la disponibilité hors ligne sont conservés.',
  resetAll:
    'La totalité du stockage local sera effacée : roadbook, paramètres, réglages, métadonnées du dernier export et état de recherche. La disponibilité hors ligne est conservée.',
} as const;

export const NOTICES = {
  exportPrepared: (fileName: string): string =>
    `Export préparé : « ${fileName} ». L’enregistrement définitif appartient au navigateur.`,
  importSucceeded: (fileName: string): string => `Importation réussie : « ${fileName} ».`,
  purgeUndone: 'Purge annulée : les POI supprimés ont été rétablis.',
  attribution: '© OpenStreetMap contributors',
  attributionUrl: 'https://www.openstreetmap.org/copyright',
  licenseNotice:
    'Un fichier roadbook exporté contient des données issues d’une base sous licence ODbL 1.0 ; sa rediffusion publique est soumise aux obligations de cette licence.',
  noAccount:
    'L’application n’exige aucun compte et n’émet aucune requête réseau en dehors d’une recherche explicitement lancée.',
  endpointNotice:
    'La recherche interroge une instance publique d’Overpass API mise à disposition par un tiers. Sa disponibilité n’est pas garantie et elle ne doit pas être sollicitée au-delà d’un usage personnel de préparation.',
  transmittedData:
    'Une requête ne contient que la géométrie du corridor interrogé et les tags des catégories actives. Ni le roadbook, ni la curation, ni les notes, ni la position courante ne sont transmis.',
} as const;

/** Limites connues et assumées, énoncées avec leur conséquence (section 3.3). */
export const KNOWN_LIMITS: readonly { readonly limit: string; readonly consequence: string }[] = [
  {
    limit: 'Parcours repassant au même endroit',
    consequence:
      'Un POI proche d’une portion parcourue plusieurs fois produit une ligne de roadbook par passage. Sa distance à la trace est propre à chaque passage et peut donc différer d’une ligne à l’autre.',
  },
  {
    limit: 'Absence de gestion des fuseaux horaires et de l’heure légale',
    consequence:
      'Toutes les heures sont locales au départ. Sur un parcours traversant un fuseau ou un changement d’heure, les heures de passage et l’interprétation des horaires d’ouverture sont décalées.',
  },
  {
    limit: 'Profil de vitesse linéaire et borné',
    consequence:
      'La vitesse suit une droite bornée par une vitesse minimale et une vitesse maximale. Au-delà des pentes de saturation, la pente n’influe plus sur la durée estimée.',
  },
  {
    limit: 'Confiance accordée aux fichiers GPX',
    consequence:
      'Aucune position aberrante n’est présumée. Un écart anormal entre deux points consécutifs est signalé mais n’empêche pas l’importation ; l’utilisateur en juge.',
  },
  {
    limit: 'Distance à la trace géométrique',
    consequence:
      'Elle ne représente ni un détour routier, ni un dénivelé d’accès, ni un temps supplémentaire.',
  },
  {
    limit: 'Base contributive',
    consequence:
      'L’absence d’un POI dans un résultat ne prouve pas son absence sur le terrain ; sa présence ne garantit ni son existence, ni son ouverture.',
  },
  {
    limit: 'D+ approché',
    consequence:
      'Le D+ est calculé sur une série rééchantillonnée et filtrée ; il peut différer des valeurs affichées par d’autres outils.',
  },
];
